import { useEffect, useState } from 'react';

import { deriveManualEntry } from '../utils/bulkManualLotEntry';
import { useForeignLotLookup } from './useForeignLotLookup';

/**
 * POM-6537 — "Enter lot manually" for Bulk lot update: the panel's draft, the lot it stages, and
 * the same checks Change NDC & lot runs (deriveManualEntry, the cross-NDC lookup, the re-key).
 *
 * The NDC is fixed by the bulk choice, so unlike the row picker there is no NDC to move to. The
 * quantity is each row's own, filled in by the plan, so the draft carries none. Everything is
 * cleared when `resetKey` (the NDC) changes or the dialog opens.
 */
const useManualLotDraft = ({
  open,
  resetKey,
  recordable,
  sessionLots,
  groups,
  ndc,
  batchNdcClaims,
  lotLookupCache,
  fulfillingPharmacyId,
  todayStr,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [lotNumber, setLotNumber] = useState('');
  const [confirmLotNumber, setConfirmLotNumber] = useState('');
  const [bud, setBud] = useState('');
  const [pickedLotId, setPickedLotId] = useState(null);
  // The entry "Add lot" staged, shown as a summary once the panel closes.
  const [selection, setSelection] = useState(null);

  const clearDraft = () => {
    setLotNumber('');
    setConfirmLotNumber('');
    setBud('');
    setPickedLotId(null);
  };

  useEffect(() => {
    setIsOpen(false);
    clearDraft();
    setSelection(null);
  }, [open, resetKey]);

  const lookup = useForeignLotLookup({
    // Not for a lot taken from the list: it is this NDC's by definition (see ChangeNdcLotDialog).
    enabled: isOpen && !pickedLotId,
    lotNumber,
    ndc,
    cache: lotLookupCache,
  });

  const entry = deriveManualEntry({
    recordable,
    sessionLots,
    availabilityGroups: groups,
    ndc,
    lotNumber,
    confirmLotNumber,
    bud,
    pickedLotId,
    fillQuantity: 0,
    foreignNdcLots: lookup.foreignNdcLots,
    batchNdcsClaimingLot: (
      batchNdcClaims?.get(
        String(lotNumber ?? '')
          .trim()
          .toLowerCase()
      ) ?? []
    ).filter((other) => other !== ndc),
    lookupFailed: lookup.lookupFailed,
    sameNdcLotsOnFile: lookup.sameNdcLotsOnFile,
    fulfillingPharmacyId,
    todayStr,
  });
  const canAdd = entry.canAdd && !lookup.isPending;

  /** The live draft once it is valid, else the staged entry; never a draft still being checked. */
  const selectionToApply = isOpen ? (canAdd && entry.selection) || null : selection;

  return {
    isOpen,
    isActive: isOpen || !!selection,
    entry,
    selection,
    selectionToApply,
    canAdd,
    isChecking: lookup.isPending,
    fields: { lotNumber, confirmLotNumber },
    // Also the restart on another facility: what was checked there does not carry over.
    start: () => {
      clearDraft();
      setSelection(null);
      setIsOpen(true);
    },
    edit: () => {
      if (!selection) return;
      setLotNumber(selection.lotNumber ?? '');
      setConfirmLotNumber(selection.lotNumber ?? '');
      setBud(selection.expirationDate ?? '');
      setPickedLotId(selection.inventoryLotId ?? null);
      setIsOpen(true);
    },
    // Cancelling the panel drops the draft only; Remove drops the staged entry too.
    dismiss: () => {
      setIsOpen(false);
      clearDraft();
    },
    remove: () => {
      setIsOpen(false);
      clearDraft();
      setSelection(null);
    },
    add: () => {
      if (!entry.selection || lookup.isPending) return;
      setSelection(entry.selection);
      setIsOpen(false);
    },
    changeLotNumber: (value) => {
      setLotNumber(value);
      setPickedLotId(null);
    },
    setConfirmLotNumber,
    setBud,
    // As ChangeNdcLotDialog's handlePickRecordableLot: a picked lot brings its number and BUD.
    pickLot: (value) => {
      if (typeof value === 'string' || value === null) {
        setLotNumber(typeof value === 'string' ? value : '');
        if (pickedLotId) setBud('');
        setPickedLotId(null);

        return;
      }
      setLotNumber(value.lotNumber);
      setConfirmLotNumber('');
      setPickedLotId(value.inventoryLotId ?? null);
      if (value.expirationDate) setBud(String(value.expirationDate).slice(0, 10));
    },
  };
};

export default useManualLotDraft;
